import {
  CheckIcon,
  EllipsisIcon,
  LinkIcon,
  LockIcon,
  RotateCwIcon,
  XIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import type { InAppBrowserApp } from '@/lib/posthog/inAppBrowser';
import {
  type MobileOs,
  urlWithoutHandoffFailed,
} from '@/modules/auth/inAppHandoff';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/modules/ui/components/sheet/Sheet';
import { PressAndHoldLink } from '@/pages/signUp/copyLink/PressAndHoldLink';
import { useCopyLink } from '@/pages/signUp/copyLink/useCopyLink';

const appNames: Record<InAppBrowserApp, string> = {
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  instagram: 'Instagram',
};

// The menu labels as each app shows them on its browser's ⋯ menu. Check them
// on a real phone when an app changes its browser.
const openInBrowserLabels: Record<InAppBrowserApp, string> = {
  linkedin: 'Open in browser',
  facebook: 'Open in external browser',
  instagram: 'Open in external browser',
};

interface InAppBrowserSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: InAppBrowserApp;
  os: MobileOs;
  /** Tries the hand-off again; hidden when the phone has none (`os: 'other'`). */
  onRetry: () => void;
}

/**
 * Shown when Google sign-in needs the real browser and the page couldn't get
 * there by itself: how to open it from the app's menu and a copyable link.
 * Opened by `InAppGoogleSignUp`.
 */
export const InAppBrowserSheet = ({
  open,
  onOpenChange,
  app,
  os,
  onRetry,
}: InAppBrowserSheetProps) => {
  const appName = appNames[app];
  const title =
    os === 'ios'
      ? 'Open this page in Safari'
      : 'Open this page in your browser';
  const description =
    os === 'ios'
      ? `Google doesn’t allow sign-in inside ${appName}’s browser. If Safari didn’t open, you can open it from ${appName}’s menu.`
      : os === 'android'
        ? `Google doesn’t allow sign-in inside ${appName}’s browser, and your browser didn’t open. You can open it from ${appName}’s menu.`
        : `Google doesn’t allow sign-in inside ${appName}’s browser. You can open this page in your browser from ${appName}’s menu.`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        // The sheet renders under <body>, outside SignUpLayout's `.portal`
        // wrapper, so it brings the portal's typeface and tokens itself.
        className="portal mx-auto max-w-[520px] gap-5 rounded-t-[20px] border-none px-6 pt-7 pb-[max(2rem,env(safe-area-inset-bottom))]"
      >
        <div className="flex flex-col gap-1.5 pr-6">
          <SheetTitle className="text-foreground text-xl">{title}</SheetTitle>
          <SheetDescription className="text-muted-foreground text-sm leading-normal">
            {description}
          </SheetDescription>
        </div>

        <div className="bg-muted flex flex-col gap-3 rounded-xl p-3.5">
          <MenuPicture />
          <ol className="text-foreground flex flex-col gap-2.5 text-sm">
            <Step number={1}>Tap ⋯ in the top right corner</Step>
            <Step number={2}>Choose {openInBrowserLabels[app]}</Step>
          </ol>
        </div>

        <div className="flex gap-2">
          {os !== 'other' && (
            <Button
              variant="outline"
              onClick={onRetry}
              className="h-11 flex-1 rounded-lg font-medium"
            >
              <RotateCwIcon />
              Try again
            </Button>
          )}
          <CopyLinkButton />
        </div>
      </SheetContent>
    </Sheet>
  );
};

/** Decorative: a sketch of the in-app browser's top bar with its ⋯ menu circled. */
const MenuPicture = () => (
  <div
    aria-hidden="true"
    className="border-border text-muted-foreground flex h-11 items-center justify-between rounded-lg border bg-white px-3"
  >
    <XIcon className="size-[18px]" />
    <span className="flex items-center gap-1 text-[13px]">
      <LockIcon className="text-muted-foreground size-3" />
      {window.location.host}
    </span>
    <span className="border-primary text-primary bg-secondary-150 flex size-[30px] items-center justify-center rounded-full border-2">
      <EllipsisIcon className="size-4" />
    </span>
  </div>
);

const Step = ({
  number,
  children,
}: {
  number: number;
  children: ReactNode;
}) => (
  <li className="flex items-center gap-2.5">
    <span className="border-input text-muted-foreground flex size-5 shrink-0 items-center justify-center rounded-full border bg-white text-[11px] font-semibold">
      {number}
    </span>
    {children}
  </li>
);

/**
 * Copies the page URL without the fallback marker; the confirmation shows on the button itself. Some
 * in-app browsers refuse clipboard access, so then the link shows as text to
 * long-press instead.
 */
const CopyLinkButton = () => {
  const trackEvent = useTrackEvent();
  const { state, copy } = useCopyLink();

  const onClick = async () => {
    trackEvent('altMethodChosen', { method: 'copyLink' });
    // `handoff=failed` only marks Android's fallback page; copied into the
    // app again it would reopen this sheet.
    await copy(urlWithoutHandoffFailed(window.location.href));
  };

  if (state.status === 'failed') {
    return <PressAndHoldLink url={state.url} className="flex-1" />;
  }

  return (
    <Button
      variant="outline"
      onClick={onClick}
      className="h-11 flex-1 rounded-lg font-medium"
    >
      {state.status === 'copied' ? <CheckIcon /> : <LinkIcon />}
      {state.status === 'copied' ? 'Link copied' : 'Copy link'}
    </Button>
  );
};
